import { X, History, Wrench, Maximize2, Minimize2 } from 'lucide-react'
import { useSidePanelStore } from '@/stores/sidePanelStore'
import HistoryPanel from './HistoryPanel'
import VendorToolbagPanel from './VendorToolbagPanel'
import CardDetailModal from '@/components/collection/CardDetailModal'

interface SidePanelProps {
  isVendorMode: boolean
}

export default function SidePanel({ isVendorMode }: SidePanelProps) {
  const { isOpen, size, activeTab, setActiveTab, close, toggleSize, hoverPreviewCard, setHoverPreview, scheduleDismissPreview, cancelDismissPreview } = useSidePanelStore()

  if (!isOpen) return null

  const tabs = [
    { id: 'history' as const, label: 'History', icon: History },
    ...(isVendorMode
      ? [{ id: 'toolbag' as const, label: 'Toolbag', icon: Wrench }]
      : []),
  ]

  const widthClass = size === 'expanded' ? 'w-[36rem]' : 'w-80'

  return (
    <>
      <div
        className={`hidden lg:flex flex-col flex-shrink-0 ${widthClass} h-[calc(100vh-4rem)] sticky top-16 z-40 bg-white dark:bg-dark-surface border-l border-gray-200 dark:border-dark-border shadow-xl transition-[width] duration-300 ease-in-out`}
      >
        {/* Header with tabs */}
        <div className="flex items-center border-b border-gray-200 dark:border-dark-border flex-shrink-0">
          <div className="flex flex-1">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                      : 'text-gray-500 dark:text-dark-muted hover:text-gray-700 dark:hover:text-dark-text'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {tab.label}
                </button>
              )
            })}
          </div>
          <button
            onClick={toggleSize}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-dark-text transition-colors"
            title={size === 'expanded' ? 'Compact view' : 'Expanded view'}
          >
            {size === 'expanded' ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={close}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-dark-text transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'history' && <HistoryPanel expanded={size === 'expanded'} />}
          {activeTab === 'toolbag' && isVendorMode && <VendorToolbagPanel />}
        </div>
      </div>

      {/* Floating hover preview - positioned to the left of the panel */}
      {hoverPreviewCard && (
        <div
          className="hidden lg:block fixed z-50 top-16 bottom-0"
          style={{ right: size === 'expanded' ? '36rem' : '20rem' }}
          onMouseEnter={cancelDismissPreview}
          onMouseLeave={scheduleDismissPreview}
        >
          <div className="w-[28rem] h-full overflow-y-auto bg-white dark:bg-dark-surface border-l border-gray-200 dark:border-dark-border shadow-2xl">
            <CardDetailModal
              card={hoverPreviewCard}
              isOpen={true}
              onClose={scheduleDismissPreview}
              onCardChange={setHoverPreview}
              quantity={0}
              inline={true}
              skipHistory={true}
            />
          </div>
        </div>
      )}
    </>
  )
}
